import { UndoChip as UndoChipPrimitive } from "@ando/ui";
import * as React from "react";

/**
 * Chat-feature UndoChip — drives the conic-gradient countdown.
 *
 * Wraps `@ando/ui`'s `UndoChipPrimitive` (which is presentation-only) with
 * a 30-second `setInterval` timer. When the timer hits zero we auto-fire
 * `onExpire` so the chip dismisses itself. Clicking the chip invokes
 * `onUndo` and stops the timer.
 *
 * Used after Ando auto-routes a query (e.g. opens a document/canvas
 * without asking) — the chip stays for 30 s as the user's escape hatch.
 */

const DEFAULT_DURATION_MS = 30_000;
const TICK_MS = 100;

export interface ChatUndoChipProps {
  label: React.ReactNode;
  actionLabel?: React.ReactNode;
  /** Total countdown in ms. Default 30 000. */
  durationMs?: number;
  /** Fired when the user clicks the chip. */
  onUndo?: () => void;
  /** Fired when the countdown reaches zero without an undo click. */
  onExpire?: () => void;
}

export const ChatUndoChip: React.FC<ChatUndoChipProps> = ({
  label,
  actionLabel,
  durationMs = DEFAULT_DURATION_MS,
  onUndo,
  onExpire,
}) => {
  const [progress, setProgress] = React.useState(100);
  const startRef = React.useRef<number>(0);

  React.useEffect(() => {
    startRef.current = performance.now();
    const id = window.setInterval(() => {
      const elapsed = performance.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / durationMs) * 100);
      setProgress(pct);
      if (pct <= 0) {
        window.clearInterval(id);
        onExpire?.();
      }
    }, TICK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [durationMs, onExpire]);

  const handleUndo = React.useCallback(() => {
    onUndo?.();
  }, [onUndo]);

  return (
    <UndoChipPrimitive
      label={label}
      actionLabel={actionLabel}
      progress={progress}
      onUndo={handleUndo}
    />
  );
};
ChatUndoChip.displayName = "ChatUndoChip";
